import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, X, Edit2, AlertTriangle } from 'lucide-react';
import { api } from '../services/api';

interface Role {
  id: string;
  name: string;
  description?: string;
  is_active: boolean;
  contact_count: number;
  created_at: string;
}

const RoleManagementPage: React.FC = () => {
  const qc = useQueryClient();

  const [showCreate, setShowCreate] = useState(false);
  const [editTarget, setEditTarget] = useState<Role | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<Role | null>(null);
  const [formName, setFormName] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const { data: roles = [], isLoading } = useQuery<Role[]>({
    queryKey: ['roles-all-admin'],
    queryFn: () => api.getRoles({ include_inactive: true }),
  });

  const openCreate = () => {
    setFormName('');
    setFormDesc('');
    setFormError('');
    setShowCreate(true);
  };

  const openEdit = (r: Role) => {
    setEditTarget(r);
    setFormName(r.name);
    setFormDesc(r.description ?? '');
    setFormError('');
  };

  const handleCreate = async () => {
    if (!formName.trim()) { setFormError('Name is required.'); return; }
    setSaving(true);
    setFormError('');
    try {
      await api.createRole({ name: formName.trim(), description: formDesc.trim() || undefined });
      qc.invalidateQueries({ queryKey: ['roles-all-admin'] });
      qc.invalidateQueries({ queryKey: ['roles-all'] });
      setShowCreate(false);
    } catch (err: any) {
      setFormError(err?.response?.data?.error ?? 'Failed to create role.');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async () => {
    if (!editTarget) return;
    if (!formName.trim()) { setFormError('Name is required.'); return; }
    setSaving(true);
    setFormError('');
    try {
      await api.updateRole(editTarget.id, { name: formName.trim(), description: formDesc.trim() || undefined });
      qc.invalidateQueries({ queryKey: ['roles-all-admin'] });
      qc.invalidateQueries({ queryKey: ['roles-all'] });
      setEditTarget(null);
    } catch (err: any) {
      setFormError(err?.response?.data?.error ?? 'Failed to update role.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    if (!deactivateTarget) return;
    try {
      await api.deactivateRole(deactivateTarget.id);
      qc.invalidateQueries({ queryKey: ['roles-all-admin'] });
      qc.invalidateQueries({ queryKey: ['roles-all'] });
      setDeactivateTarget(null);
    } catch (err: any) {
      alert(err?.response?.data?.error ?? 'Failed to deactivate role.');
    }
  };

  const activeRoles = roles.filter((r) => r.is_active);
  const inactiveRoles = roles.filter((r) => !r.is_active);

  return (
    <div style={{ padding: '24px 32px', maxWidth: 860 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 900 }}>Role Management</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--gray-500)', fontSize: 14 }}>
            {activeRoles.length} active role{activeRoles.length !== 1 ? 's' : ''}
          </p>
        </div>
        <button className="btn btn-primary" onClick={openCreate} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Plus size={14} /> New Role
        </button>
      </div>

      {isLoading ? (
        <p style={{ color: 'var(--gray-500)' }}>Loading…</p>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600 }}>Role Name</th>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600 }}>Description</th>
                <th style={{ padding: '10px 16px', textAlign: 'center', fontWeight: 600 }}>Contacts</th>
                <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: 600 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {activeRoles.map((r) => (
                <tr key={r.id} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                  <td style={{ padding: '10px 16px', fontWeight: 500 }}>{r.name}</td>
                  <td style={{ padding: '10px 16px', color: 'var(--gray-500)' }}>{r.description ?? '—'}</td>
                  <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                    <span style={{ background: 'var(--gray-100)', borderRadius: 10, padding: '2px 8px', fontWeight: 600 }}>
                      {r.contact_count}
                    </span>
                  </td>
                  <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                    <button
                      className="btn btn-ghost"
                      style={{ fontSize: 12, marginRight: 4 }}
                      onClick={() => openEdit(r)}
                    >
                      <Edit2 size={12} /> Edit
                    </button>
                    <button
                      className="btn btn-ghost"
                      style={{ fontSize: 12, color: 'var(--red)' }}
                      onClick={() => setDeactivateTarget(r)}
                    >
                      Deactivate
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {inactiveRoles.length > 0 && (
            <>
              <div style={{ padding: '8px 16px', background: 'var(--gray-50)', borderTop: '1px solid var(--gray-200)', fontSize: 12, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Inactive
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <tbody>
                  {inactiveRoles.map((r) => (
                    <tr key={r.id} style={{ borderBottom: '1px solid var(--gray-100)', opacity: 0.6 }}>
                      <td style={{ padding: '8px 16px', fontWeight: 500 }}>{r.name}</td>
                      <td style={{ padding: '8px 16px', color: 'var(--gray-500)' }}>{r.description ?? '—'}</td>
                      <td style={{ padding: '8px 16px', textAlign: 'center' }}>{r.contact_count}</td>
                      <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                        <button
                          className="btn btn-ghost"
                          style={{ fontSize: 12 }}
                          onClick={() => openEdit(r)}
                        >
                          <Edit2 size={12} /> Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}

      {/* Create Modal */}
      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal" style={{ maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0 }}>New Role</h3>
              <button className="btn btn-ghost" onClick={() => setShowCreate(false)}><X size={16} /></button>
            </div>
            <label className="field-label">Role Name *</label>
            <input
              className="field-input"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="e.g. CHEMPACK - Admin Point of Contact"
              autoFocus
            />
            <label className="field-label" style={{ marginTop: 10 }}>Description</label>
            <input
              className="field-input"
              value={formDesc}
              onChange={(e) => setFormDesc(e.target.value)}
              placeholder="Optional description"
            />
            {formError && <p style={{ color: 'var(--red)', fontSize: 12, marginTop: 8 }}>{formError}</p>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button className="btn btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
              <button className="btn btn-primary" disabled={saving} onClick={handleCreate}>
                {saving ? 'Creating…' : 'Create Role'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editTarget && (
        <div className="modal-overlay" onClick={() => setEditTarget(null)}>
          <div className="modal" style={{ maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0 }}>Edit Role</h3>
              <button className="btn btn-ghost" onClick={() => setEditTarget(null)}><X size={16} /></button>
            </div>
            <label className="field-label">Role Name *</label>
            <input
              className="field-input"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              autoFocus
            />
            <label className="field-label" style={{ marginTop: 10 }}>Description</label>
            <input
              className="field-input"
              value={formDesc}
              onChange={(e) => setFormDesc(e.target.value)}
              placeholder="Optional description"
            />
            {formError && <p style={{ color: 'var(--red)', fontSize: 12, marginTop: 8 }}>{formError}</p>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button className="btn btn-secondary" onClick={() => setEditTarget(null)}>Cancel</button>
              <button className="btn btn-primary" disabled={saving} onClick={handleEdit}>
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deactivate Confirmation */}
      {deactivateTarget && (
        <div className="modal-overlay" onClick={() => setDeactivateTarget(null)}>
          <div className="modal" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 16 }}>
              <AlertTriangle size={20} color="var(--red)" style={{ flexShrink: 0, marginTop: 2 }} />
              <div>
                <h3 style={{ margin: '0 0 6px' }}>Deactivate "{deactivateTarget.name}"?</h3>
                {deactivateTarget.contact_count > 0 ? (
                  <p style={{ margin: 0, fontSize: 14, color: 'var(--gray-600)' }}>
                    This role is assigned to <strong>{deactivateTarget.contact_count}</strong> contact{deactivateTarget.contact_count !== 1 ? 's' : ''}. Reassign or remove before deactivating.
                  </p>
                ) : (
                  <p style={{ margin: 0, fontSize: 14, color: 'var(--gray-600)' }}>
                    This role has no contacts assigned. It will be hidden from all role selectors.
                  </p>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-secondary" onClick={() => setDeactivateTarget(null)}>Cancel</button>
              <button
                className="btn btn-primary"
                style={{ background: 'var(--red)' }}
                disabled={deactivateTarget.contact_count > 0}
                onClick={handleDeactivate}
              >
                Deactivate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleManagementPage;
